import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { Trash2, LogIn, MapPin, Users, Wheat, BarChart3, LogOut, RefreshCw, QrCode, Plus, Download, Package, Eye, ShoppingBag, Truck, CheckCircle, Clock, XCircle } from 'lucide-react';
import { BACKEND_URL } from '../config';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const farmerIcon = new L.Icon({
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

export default function AdminPanel() {
  const [token, setToken] = useState(sessionStorage.getItem('admin_token') || '');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [farmers, setFarmers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState('farmers');
  
  // Product QR Management States
  const [products, setProducts] = useState([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [productForm, setProductForm] = useState({
    product_name: '', category: 'Food & Grains', net_weight: '', mrp: '',
    mfg_date: '', best_before: '', batch_no: '', fssai_lic: '', ingredients: '', benefits: ''
  });
  const [creatingProduct, setCreatingProduct] = useState(false);
  const [selectedQR, setSelectedQR] = useState(null);
  
  // Orders Management States
  const [orders, setOrders] = useState([]);
  const [updatingOrder, setUpdatingOrder] = useState(null);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
    try {
      const [farmersRes, statsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/admin/farmers`, { headers }),
        fetch(`${BACKEND_URL}/api/admin/dashboard`, { headers }),
      ]);
      if (farmersRes.status === 401 || statsRes.status === 401) {
        setToken(''); sessionStorage.removeItem('admin_token'); return;
      }
      const farmersData = await farmersRes.json();
      const statsData = await statsRes.json();
      setFarmers(farmersData.farmers || []);
      setStats(statsData);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') console.error('Failed to fetch data:', err);
    } finally { setLoading(false); }
  }, [token]);

  const fetchProducts = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/products`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setProducts(data.products || []); }
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error(err); }
  }, [token]);

  const fetchOrders = useCallback(async () => {
    if (!token) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/orders`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) { const data = await res.json(); setOrders(data.orders || []); }
    } catch (err) { if (process.env.NODE_ENV === 'development') console.error(err); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { if (activeTab === 'products') fetchProducts(); }, [activeTab, fetchProducts]);
  useEffect(() => { if (activeTab === 'orders') fetchOrders(); }, [activeTab, fetchOrders]);
  useEffect(() => { if (!token) return; const interval = setInterval(fetchData, 30000); return () => clearInterval(interval); }, [token, fetchData]);

  const handleLogin = async (e) => {
    e.preventDefault(); setLoginError('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) { setLoginError('Invalid credentials'); return; }
      const data = await res.json();
      setToken(data.token); sessionStorage.setItem('admin_token', data.token);
    } catch { setLoginError('Login failed'); }
  };

  const handleLogout = () => {
    setToken(''); sessionStorage.removeItem('admin_token');
    setFarmers([]); setStats(null); setProducts([]); setOrders([]);
  };

  const handleDelete = async (farmerId, farmerName) => {
    if (!window.confirm(`Delete farmer "${farmerName}"?`)) return;
    setDeleting(farmerId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/farmers/${farmerId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        setFarmers(prev => prev.filter(f => f.id !== farmerId));
        if (stats) setStats(prev => ({ ...prev, total_farmers: prev.total_farmers - 1 }));
      }
    } catch (err) { console.error(err); }
    finally { setDeleting(null); }
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault(); setCreatingProduct(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/products/create`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...productForm,
          ingredients: productForm.ingredients.split(',').map(i => i.trim()).filter(i => i),
          benefits: productForm.benefits.split(',').map(b => b.trim()).filter(b => b)
        })
      });
      if (res.ok) {
        const data = await res.json();
        setShowProductModal(false);
        setProductForm({ product_name: '', category: 'Food & Grains', net_weight: '', mrp: '', mfg_date: '', best_before: '', batch_no: '', fssai_lic: '', ingredients: '', benefits: '' });
        fetchProducts(); setSelectedQR(data);
      }
    } catch (err) { console.error(err); } finally { setCreatingProduct(false); }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Delete this product?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/products/${productId}`, {
        method: 'DELETE', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setProducts(prev => prev.filter(p => p.product_id !== productId));
    } catch (err) { console.error(err); }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    setUpdatingOrder(orderId);
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/orders/${orderId}/status?status=${newStatus}`, {
        method: 'PUT', headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setOrders(prev => prev.map(o => o.order_id === orderId ? { ...o, order_status: newStatus } : o));
    } catch (err) { console.error(err); } finally { setUpdatingOrder(null); }
  };

  const downloadQR = (qrBase64, productId) => {
    const link = document.createElement('a');
    link.href = qrBase64; link.download = `QR-${productId}.png`; link.click();
  };

  const getStatusIcon = (status) => {
    const icons = { pending: <Clock size={14} />, confirmed: <CheckCircle size={14} />, processing: <Package size={14} />, shipped: <Truck size={14} />, delivered: <CheckCircle size={14} />, cancelled: <XCircle size={14} /> };
    return icons[status] || <Clock size={14} />;
  };

  // Login screen
  if (!token) {
    return (
      <div className="admin-login-page">
        <form className="admin-login-form" onSubmit={handleLogin} data-testid="admin-login-form">
          <div className="admin-login-logo"><MapPin size={32} /> BoAI Admin</div>
          <h2>Admin Panel Login</h2>
          {loginError && <div className="admin-error">{loginError}</div>}
          <input type="text" placeholder="Username" value={username} onChange={e => setUsername(e.target.value)} required data-testid="admin-username" />
          <input type="password" placeholder="Password" value={password} onChange={e => setPassword(e.target.value)} required data-testid="admin-password" />
          <button type="submit" data-testid="admin-login-btn"><LogIn size={16} /> Login</button>
        </form>
      </div>
    );
  }

  const farmersWithGeo = farmers.filter(f => f.geo && f.geo.lat && f.geo.lng);
  const pendingOrders = orders.filter(o => o.order_status === 'pending' || o.order_status === 'confirmed');

  return (
    <div className="admin-panel" data-testid="admin-panel">
      {/* Header */}
      <div className="admin-header">
        <div className="admin-header-left">
          <MapPin size={20} /><h1>BoAI Admin Panel</h1><span className="admin-live-badge">LIVE</span>
        </div>
        <div className="admin-header-right">
          <button className="admin-refresh-btn" onClick={() => { fetchData(); fetchProducts(); fetchOrders(); }} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spin' : ''} /> Refresh
          </button>
          <button className="admin-logout-btn" onClick={handleLogout}><LogOut size={16} /> Logout</button>
        </div>
      </div>

      {/* Tabs */}
      <div className="admin-tabs">
        <button className={`admin-tab ${activeTab === 'farmers' ? 'active' : ''}`} onClick={() => setActiveTab('farmers')}>
          <Users size={18} /> Farmers & Map
        </button>
        <button className={`admin-tab ${activeTab === 'orders' ? 'active' : ''}`} onClick={() => setActiveTab('orders')}>
          <ShoppingBag size={18} /> Orders {pendingOrders.length > 0 && <span className="tab-badge">{pendingOrders.length}</span>}
        </button>
        <button className={`admin-tab ${activeTab === 'products' ? 'active' : ''}`} onClick={() => setActiveTab('products')}>
          <QrCode size={18} /> Product QR
        </button>
      </div>

      {/* Farmers Tab */}
      {activeTab === 'farmers' && (
        <>
          {stats && (
            <div className="admin-stats-row">
              <div className="admin-stat"><Users size={20} /><div><span className="stat-num">{stats.total_farmers}</span><span className="stat-label">Farmers</span></div></div>
              <div className="admin-stat"><Wheat size={20} /><div><span className="stat-num">{stats.total_crop_submissions}</span><span className="stat-label">Crops</span></div></div>
              <div className="admin-stat"><BarChart3 size={20} /><div><span className="stat-num">{stats.total_users}</span><span className="stat-label">Users</span></div></div>
              <div className="admin-stat"><MapPin size={20} /><div><span className="stat-num">{farmersWithGeo.length}</span><span className="stat-label">Geo-tagged</span></div></div>
            </div>
          )}
          <div className="admin-map-container" data-testid="admin-map">
            <MapContainer center={[22.5, 78.5]} zoom={5} style={{ height: '400px', width: '100%', borderRadius: '16px' }}>
              <TileLayer attribution='&copy; OpenStreetMap' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              {farmersWithGeo.map(farmer => (
                <Marker key={farmer.id} position={[farmer.geo.lat, farmer.geo.lng]} icon={farmerIcon}>
                  <Popup><strong>{farmer.name}</strong><br />{farmer.village}, {farmer.state}<br />Crop: {farmer.crop_type}</Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
          <div className="admin-table-container">
            <h3>Registered Farmers ({farmers.length})</h3>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead><tr><th>Name</th><th>Phone</th><th>Village</th><th>State</th><th>Crop</th><th>Land</th><th>Status</th><th>Action</th></tr></thead>
                <tbody>
                  {farmers.map(f => (
                    <tr key={f.id}>
                      <td><strong>{f.name}</strong></td><td>{f.phone}</td><td>{f.village}</td><td>{f.state}</td><td>{f.crop_type}</td><td>{f.land_size}</td>
                      <td><span className={`status-badge status-${f.status}`}>{f.status}</span></td>
                      <td><button className="delete-btn" onClick={() => handleDelete(f.id, f.name)} disabled={deleting === f.id}><Trash2 size={14} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* Orders Tab */}
      {activeTab === 'orders' && (
        <div className="admin-orders-section">
          <div className="orders-header">
            <h3><ShoppingBag size={20} /> Order Management</h3>
            <div className="orders-summary">
              <span className="summary-item pending">{orders.filter(o => o.payment_status === 'pending').length} Payment Pending</span>
              <span className="summary-item confirmed">{orders.filter(o => o.order_status === 'confirmed').length} To Fulfill</span>
              <span className="summary-item shipped">{orders.filter(o => o.order_status === 'shipped').length} Shipped</span>
            </div>
          </div>
          
          {orders.length === 0 ? (
            <div className="no-orders"><ShoppingBag size={48} /><p>No orders yet</p></div>
          ) : (
            <div className="orders-list">
              {orders.map(order => (
                <div key={order.order_id} className={`order-card ${order.order_status}`} data-testid={`order-${order.order_id}`}>
                  <div className="order-header">
                    <div className="order-id-info">
                      <span className="order-id">{order.order_id}</span>
                      <span className="order-date">{new Date(order.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <div className="order-badges">
                      <span className={`payment-badge ${order.payment_status}`}>
                        {order.payment_status === 'paid' ? '✓ Paid' : '⏳ Payment Pending'}
                      </span>
                      <span className={`status-badge order-status-${order.order_status}`}>
                        {getStatusIcon(order.order_status)} {order.order_status}
                      </span>
                    </div>
                  </div>
                  
                  <div className="order-body">
                    <div className="order-product">
                      <strong>{order.product_name}</strong>
                      <span className="order-amount">₹{order.total_amount}</span>
                    </div>
                    
                    <div className="order-customer">
                      <div className="customer-name">{order.customer?.name}</div>
                      <div className="customer-contact">{order.customer?.phone} • {order.customer?.email}</div>
                      <div className="customer-address">
                        {order.customer?.address}, {order.customer?.city}, {order.customer?.state} - {order.customer?.pincode}
                      </div>
                    </div>
                  </div>

                  <div className="order-actions">
                    <select 
                      value={order.order_status} 
                      onChange={(e) => handleUpdateOrderStatus(order.order_id, e.target.value)}
                      disabled={updatingOrder === order.order_id}
                      className="status-select"
                    >
                      <option value="pending">Pending</option>
                      <option value="confirmed">Confirmed</option>
                      <option value="processing">Processing</option>
                      <option value="shipped">Shipped</option>
                      <option value="delivered">Delivered</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Products Tab */}
      {activeTab === 'products' && (
        <div className="admin-products-section">
          <div className="products-header">
            <h3><Package size={20} /> Product QR Management</h3>
            <button className="btn-primary" onClick={() => setShowProductModal(true)} data-testid="add-product-btn">
              <Plus size={18} /> Add Product
            </button>
          </div>
          <div className="products-grid">
            {products.length === 0 ? (
              <div className="no-products"><QrCode size={48} /><p>No products yet</p></div>
            ) : (
              products.map(product => (
                <div key={product.product_id} className="product-card">
                  <div className="product-qr">{product.qr_code ? <img src={product.qr_code} alt="QR" /> : <QrCode size={80} />}</div>
                  <div className="product-details">
                    <h4>{product.product_name}</h4>
                    <p className="product-id">{product.product_id}</p>
                    <p className="product-meta">{product.net_weight} • ₹{product.mrp}</p>
                    <p className="product-scans">Scans: {product.scan_count || 0}</p>
                  </div>
                  <div className="product-actions">
                    <button className="action-btn view-btn" onClick={() => window.open(`/verify/${product.product_id}`, '_blank')}><Eye size={16} /></button>
                    {product.qr_code && <button className="action-btn download-btn" onClick={() => downloadQR(product.qr_code, product.product_id)}><Download size={16} /></button>}
                    <button className="action-btn delete-btn" onClick={() => handleDeleteProduct(product.product_id)}><Trash2 size={16} /></button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Create Product Modal */}
      {showProductModal && (
        <div className="admin-modal-overlay" onClick={() => setShowProductModal(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3><Package size={20} /> Create Product</h3><button className="modal-close" onClick={() => setShowProductModal(false)}>×</button></div>
            <form onSubmit={handleCreateProduct} className="product-form">
              <div className="form-row">
                <div className="form-group"><label>Product Name *</label><input type="text" value={productForm.product_name} onChange={e => setProductForm({...productForm, product_name: e.target.value})} required /></div>
                <div className="form-group"><label>Category</label><select value={productForm.category} onChange={e => setProductForm({...productForm, category: e.target.value})}><option>Food & Grains</option><option>Oats & Cereals</option><option>Ghee & Oils</option><option>Spices</option></select></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Net Weight *</label><input type="text" value={productForm.net_weight} onChange={e => setProductForm({...productForm, net_weight: e.target.value})} required /></div>
                <div className="form-group"><label>MRP (₹) *</label><input type="number" value={productForm.mrp} onChange={e => setProductForm({...productForm, mrp: e.target.value})} required /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Mfg Date</label><input type="text" value={productForm.mfg_date} onChange={e => setProductForm({...productForm, mfg_date: e.target.value})} /></div>
                <div className="form-group"><label>Best Before</label><input type="text" value={productForm.best_before} onChange={e => setProductForm({...productForm, best_before: e.target.value})} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Batch No.</label><input type="text" value={productForm.batch_no} onChange={e => setProductForm({...productForm, batch_no: e.target.value})} /></div>
                <div className="form-group"><label>FSSAI License</label><input type="text" value={productForm.fssai_lic} onChange={e => setProductForm({...productForm, fssai_lic: e.target.value})} /></div>
              </div>
              <div className="form-group full-width"><label>Ingredients (comma separated)</label><input type="text" value={productForm.ingredients} onChange={e => setProductForm({...productForm, ingredients: e.target.value})} /></div>
              <div className="form-group full-width"><label>Benefits (comma separated)</label><input type="text" value={productForm.benefits} onChange={e => setProductForm({...productForm, benefits: e.target.value})} /></div>
              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowProductModal(false)}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={creatingProduct}>{creatingProduct ? 'Creating...' : <><QrCode size={16} /> Create & Generate QR</>}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* QR Code Modal */}
      {selectedQR && (
        <div className="admin-modal-overlay" onClick={() => setSelectedQR(null)}>
          <div className="admin-modal qr-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header"><h3><QrCode size={20} /> QR Generated!</h3><button className="modal-close" onClick={() => setSelectedQR(null)}>×</button></div>
            <div className="qr-display">
              <img src={selectedQR.qr_code} alt="QR Code" />
              <p className="product-id-display">{selectedQR.product_id}</p>
              <button className="btn-primary" onClick={() => downloadQR(selectedQR.qr_code, selectedQR.product_id)}><Download size={18} /> Download QR</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
